import { useMemo, useState, useCallback } from 'react';
import { ReportBarChart } from '@/components/reports/shared/ReportBarChart';
import { ReportLineChart } from '@/components/reports/shared/ReportLineChart';
import { ReportPieChart } from '@/components/reports/shared/ReportPieChart';
import { ReportTable, createSortableColumn } from '@/components/reports/shared/ReportTable';
import { ReportSummaryCards } from '@/components/reports/shared/ReportSummaryCards';
import { ReportDrillDownModal } from './ReportDrillDownModal';
import type { ReportConfig, ReportDataPoint, DrillDownContext } from '@/types/custom-reports';
import { formatCurrency, formatPercent } from '@/lib/utils';

interface CustomReportVisualizationProps {
  data: ReportDataPoint[];
  config: ReportConfig;
  comparisonData?: ReportDataPoint[];
}

/**
 * Visualization router component that renders the appropriate visualization
 * based on the report configuration and data structure.
 * Includes drill-down functionality for chart elements.
 */
export function CustomReportVisualization({
  data,
  config,
  comparisonData,
}: CustomReportVisualizationProps) {
  // Drill-down modal state
  const [drillDownContext, setDrillDownContext] = useState<DrillDownContext | null>(null);
  const [isDrillDownOpen, setIsDrillDownOpen] = useState(false);

  // Handle chart element click for drill-down
  const handleDrillDown = useCallback((item: Record<string, unknown>, index: number) => {
    const context: DrillDownContext = {
      groupBy: config.groupBy!,
      groupLabel: String(item.label || ''),
      dateRange: config.dateRange,
      filters: config.filters,
    };
    setDrillDownContext(context);
    setIsDrillDownOpen(true);
  }, [config]);

  // Handle pie slice click
  const handlePieSliceClick = useCallback((item: { name: string; value: number; color?: string }, index: number) => {
    const context: DrillDownContext = {
      groupBy: config.groupBy!,
      groupLabel: item.name,
      dateRange: config.dateRange,
      filters: config.filters,
    };
    setDrillDownContext(context);
    setIsDrillDownOpen(true);
  }, [config]);
  const visualizationType = config.visualizationType;

  // Generate summary cards for non-grouped or comparison views
  const summaryCards = useMemo(() => {
    if (visualizationType === 'cards') {
      return generateSummaryCards(data, config, comparisonData);
    }
    return null;
  }, [data, config, comparisonData, visualizationType]);

  // Render summary cards
  if (visualizationType === 'cards') {
    return (
      <div className="space-y-6">
        {summaryCards && <ReportSummaryCards cards={summaryCards} columns={4} />}
        {data.length > 0 && (
          <ReportTable
            data={data}
            columns={generateTableColumns(data, config)}
            pageSize={20}
          />
        )}
      </div>
    );
  }

  // Render table visualization
  if (visualizationType === 'table') {
    return (
      <ReportTable
        data={data}
        columns={generateTableColumns(data, config)}
        pageSize={20}
      />
    );
  }

  // Render chart visualizations
  const groupBy = config.groupBy;

  // For charts, we need grouped data
  if (!groupBy) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>Selecione um agrupamento para visualizar este gráfico.</p>
      </div>
    );
  }

  const chartData = useMemo(() => {
    return transformDataForChart(data, config);
  }, [data, config]);

  const dataKeys = useMemo(() => {
    return generateChartKeys(config, comparisonData);
  }, [config, comparisonData]);

  // Bar chart
  if (visualizationType === 'bar') {
    return (
      <>
        <ReportBarChart
          data={chartData}
          dataKeys={dataKeys}
          xAxisKey={getXAxisKey(config)}
          height={400}
          showLegend={config.chartConfig?.showLegend ?? true}
          formatValue={formatCurrency}
          onBarClick={handleDrillDown}
        />
        <ReportDrillDownModal
          open={isDrillDownOpen}
          onOpenChange={setIsDrillDownOpen}
          context={drillDownContext}
        />
      </>
    );
  }

  // Line chart (best for time series)
  if (visualizationType === 'line') {
    const isTimeSeries = groupBy === 'month' || groupBy === 'week';

    return (
      <ReportLineChart
        data={chartData}
        dataKeys={dataKeys.map((key) => ({
          ...key,
          showArea: isTimeSeries,
        }))}
        xAxisKey={getXAxisKey(config)}
        height={400}
        showLegend={config.chartConfig?.showLegend ?? true}
        showDots={!isTimeSeries}
        formatValue={formatCurrency}
      />
    );
  }

  // Pie chart (best for distribution)
  if (visualizationType === 'pie') {
    const pieData = chartData.map((item) => ({
      name: item.label,
      value: Number(item.value),
    }));

    return (
      <>
        <div className="flex justify-center">
          <ReportPieChart
            data={pieData}
            height={400}
            showLegend={config.chartConfig?.showLegend ?? true}
            showPercentages={true}
            formatValue={formatCurrency}
            onSliceClick={handlePieSliceClick}
          />
        </div>
        <ReportDrillDownModal
          open={isDrillDownOpen}
          onOpenChange={setIsDrillDownOpen}
          context={drillDownContext}
        />
      </>
    );
  }

  return (
    <div className="text-center py-12 text-muted-foreground">
      <p>Tipo de visualização não suportado.</p>
    </div>
  );
}

/**
 * Generate table columns based on data structure and config
 */
function generateTableColumns(data: QueryResult[], config: ReportConfig) {
  const columns: ReturnType<typeof createSortableColumn>[] = [];

  // Add label column if grouped
  if (config.groupBy) {
    const labelKey = getLabelKey(config);
    columns.push(
      createSortableColumn(labelKey, getGroupByLabel(config.groupBy))
    );
  }

  // Add amount/value column
  columns.push(
    createSortableColumn(
      'value',
      'Valor',
      {
        cell: ({ getValue }) => formatCurrency(getValue() as number),
      }
    )
  );

  // Add comparison columns if available
  if (config.compareWith?.enabled) {
    columns.push(
      createSortableColumn(
        'previousValue',
        'Período Anterior',
        {
          cell: ({ getValue }) => {
            const value = getValue() as number | undefined;
            return value !== undefined ? formatCurrency(value) : '-';
          },
        }
      )
    );

    columns.push(
      createSortableColumn(
        'variance',
        'Variação',
        {
          cell: ({ getValue }) => {
            const value = getValue() as number | undefined;
            return value !== undefined ? formatPercent(value / 100) : '-';
          },
        }
      )
    );
  }

  // Add additional metric columns
  if (config.metrics.count) {
    columns.push(
      createSortableColumn('count', 'Quantidade')
    );
  }

  if (config.metrics.avg) {
    columns.push(
      createSortableColumn(
        'avg',
        'Média',
        {
          cell: ({ getValue }) => formatCurrency(getValue() as number),
        }
      )
    );
  }

  return columns;
}

/**
 * Transform query result data for chart consumption
 */
function transformDataForChart(
  data: ReportDataPoint[],
  config: ReportConfig
): Array<Record<string, unknown>> {
  const labelKey = getLabelKey(config);

  return data.map((item) => {
    const chartItem: Record<string, unknown> = {
      label: item[labelKey] ?? 'Sem categoria',
      value: typeof item.value === 'number' ? item.value : 0,
    };

    // Add comparison data
    if (config.compareWith?.enabled && item.previousValue !== undefined) {
      chartItem.previousValue = item.previousValue;
      chartItem.variance = item.variance ?? 0;
    }

    // Add metrics
    if (item.count !== undefined) {
      chartItem.count = item.count;
    }
    if (item.avg !== undefined) {
      chartItem.avg = item.avg;
    }

    return chartItem;
  });
}

/**
 * Generate chart keys for Recharts
 */
function generateChartKeys(
  config: ReportConfig,
  comparisonData?: QueryResult[]
): Array<{ key: string; label: string; color?: string }> {
  const keys: Array<{ key: string; label: string; color?: string }> = [
    {
      key: 'value',
      label: 'Valor Atual',
      color: 'hsl(var(--chart-1))',
    },
  ];

  if (config.compareWith?.enabled) {
    keys.push({
      key: 'previousValue',
      label: 'Período Anterior',
      color: 'hsl(var(--chart-2))',
    });
  }

  return keys;
}

/**
 * Get the X-axis key for charts based on group by
 */
function getXAxisKey(config: ReportConfig): string {
  return 'label';
}

/**
 * Get the label key from query results based on group by
 */
function getLabelKey(config: ReportConfig): string {
  switch (config.groupBy) {
    case 'category':
      return 'category';
    case 'cost_center':
      return 'costCenter';
    case 'client':
      return 'client';
    case 'supplier':
      return 'supplier';
    case 'month':
      return 'month';
    case 'week':
      return 'week';
    default:
      return 'label';
  }
}

/**
 * Get human-readable label for group by type
 */
function getGroupByLabel(groupBy: string): string {
  switch (groupBy) {
    case 'category':
      return 'Categoria';
    case 'cost_center':
      return 'Centro de Custo';
    case 'client':
      return 'Cliente';
    case 'supplier':
      return 'Fornecedor';
    case 'month':
      return 'Mês';
    case 'week':
      return 'Semana';
    default:
      return 'Grupo';
  }
}

/**
 * Generate summary cards for report overview
 */
function generateSummaryCards(
  data: ReportDataPoint[],
  config: ReportConfig,
  comparisonData?: ReportDataPoint[]
) {
  const cards = [];

  // Calculate totals
  const totalValue = data.reduce((sum, item) => sum + (item.value || 0), 0);
  const totalPrevious = data.reduce(
    (sum, item) => sum + (item.previousValue || 0),
    0
  );

  // Main value card
  cards.push({
    id: 'total',
    label: config.filters.transactionType === 'income' ? 'Total Receitas' :
          config.filters.transactionType === 'expense' ? 'Total Despesas' :
          'Total',
    value: formatCurrency(totalValue),
    trend: config.compareWith?.enabled && totalPrevious > 0 ? {
      value: ((totalValue - totalPrevious) / totalPrevious) * 100,
      isPositive: totalValue >= totalPrevious,
    } : undefined,
  });

  // Transaction count
  const totalCount = data.reduce((sum, item) => sum + (item.count || 0), 0);
  if (totalCount > 0) {
    cards.push({
      id: 'count',
      label: 'Transações',
      value: totalCount.toLocaleString('pt-BR'),
    });
  }

  // Average value
  if (totalCount > 0) {
    cards.push({
      id: 'average',
      label: 'Valor Médio',
      value: formatCurrency(totalValue / totalCount),
    });
  }

  // Top category/item
  if (data.length > 0) {
    const topItem = [...data].sort((a, b) => (b.value || 0) - (a.value || 0))[0];
    const labelKey = getLabelKey(config);
    cards.push({
      id: 'top',
      label: `Maior ${getGroupByLabel(config.groupBy || '')}`,
      value: topItem[labelKey] as string || 'N/A',
    });
  }

  return cards;
}
